import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DatePicker, DateRangePicker } from '@/components/ui/date-picker';
import { Goal } from './GoalCard';

interface GoalFormData {
  title: string;
  target: number;
  current: number;
  startDate: Date | undefined;
  endDate: Date | undefined;
  deadline?: Date | undefined;
  reviewDate?: Date | undefined;
  status: 'on-track' | 'at-risk' | 'completed';
  notes?: string;
  reviewFeedback?: string;
}

interface SubmitData {
  employeeId: string;
  title: string;
  target: number;
  current: number;
  startDate: string;
  endDate: string;
  deadline?: string;
  reviewDate?: string;
  status: 'on-track' | 'at-risk' | 'completed';
  notes?: string;
  reviewFeedback?: string;
}

interface GoalFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  goal?: Goal | null;
  onSubmit: (data: SubmitData) => void | Promise<void>;
  isSubmitting?: boolean;
  employeeId: string;
}

export function GoalFormSheet({
  open,
  onOpenChange,
  goal,
  onSubmit,
  isSubmitting = false,
  employeeId,
}: GoalFormSheetProps) {
  const [formData, setFormData] = useState<GoalFormData>({
    title: '',
    target: 100,
    current: 0,
    startDate: undefined,
    endDate: undefined,
    deadline: undefined,
    reviewDate: undefined,
    status: 'on-track',
    notes: '',
    reviewFeedback: '',
  });

  // Update form data when goal changes or sheet opens
  useEffect(() => {
    if (open) {
      setFormData({
        title: goal?.title || '',
        target: goal?.target || 100,
        current: goal?.current || 0,
        startDate: goal?.startDate ? new Date(goal.startDate) : undefined,
        endDate: goal?.endDate ? new Date(goal.endDate) : undefined,
        deadline: goal?.deadline ? new Date(goal.deadline) : undefined,
        reviewDate: goal?.reviewDate ? new Date(goal.reviewDate) : undefined,
        status: goal?.status || 'on-track',
        notes: goal?.notes || '',
        reviewFeedback: goal?.reviewFeedback || '',
      });
    }
  }, [open, goal]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Convert dates to ISO strings for API
    const submitData: SubmitData = {
      employeeId,
      ...formData,
      startDate: formData.startDate?.toISOString().split('T')[0] ?? '',
      endDate: formData.endDate?.toISOString().split('T')[0] ?? '',
      deadline: formData.deadline?.toISOString().split('T')[0],
      reviewDate: formData.reviewDate?.toISOString().split('T')[0],
    };

    onSubmit(submitData);
  };

  const handleChange = (field: keyof GoalFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className='px-6'>
          <SheetTitle>{goal ? 'Edit Goal' : 'Add New Goal'}</SheetTitle>
          <SheetDescription>
            {goal
              ? 'Update goal details and track progress'
              : 'Create a new goal for the employee'}
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-6 px-6">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="e.g., Complete 50 job postings"
              required
            />
          </div>

          {/* Target & Current Progress */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="target">Target *</Label>
              <Input
                id="target"
                type="number"
                min="0"
                step="0.01"
                value={formData.target}
                onChange={(e) => handleChange('target', parseFloat(e.target.value))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="current">Current Progress *</Label>
              <Input
                id="current"
                type="number"
                min="0"
                step="0.01"
                value={formData.current}
                onChange={(e) => handleChange('current', parseFloat(e.target.value))}
                required
              />
            </div>
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label htmlFor="status">Status *</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => handleChange('status', value)}
            >
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="on-track">On Track</SelectItem>
                <SelectItem value="at-risk">At Risk</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Date Range */}
          <div className="space-y-2">
            <DateRangePicker
              startDate={formData.startDate}
              endDate={formData.endDate}
              onStartDateChange={(date) => handleChange('startDate', date)}
              onEndDateChange={(date) => handleChange('endDate', date)}
              disabled={isSubmitting}
            />
          </div>

          {/* Optional Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="deadline">Deadline</Label>
              <DatePicker
                date={formData.deadline}
                onSelect={(date) => handleChange('deadline', date)}
                placeholder="Select deadline"
                disabled={isSubmitting}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reviewDate">Review Date</Label>
              <DatePicker
                date={formData.reviewDate}
                onSelect={(date) => handleChange('reviewDate', date)}
                placeholder="Select review date"
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Add any additional notes or context..."
              rows={3}
            />
          </div>

          {/* Review Feedback (only for edit) */}
          {goal && (
            <div className="space-y-2">
              <Label htmlFor="reviewFeedback">Review Feedback</Label>
              <Textarea
                id="reviewFeedback"
                value={formData.reviewFeedback}
                onChange={(e) => handleChange('reviewFeedback', e.target.value)}
                placeholder="Add feedback from performance review..."
                rows={3}
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : goal ? 'Update Goal' : 'Create Goal'}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
